import { EditNode, DeleteMenuItem } from "src/enterprise/ra-tree";
import { useParams } from "react-router-dom";
import React, { useContext, useEffect } from "react";
import {
  BooleanInput,
  ReferenceInput,
  AutocompleteInput,
  SaveButton,
  SelectArrayInput,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  ToolbarProps,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
  required,
} from "react-admin";
import { NavigationTypeEnum, RoleEnum, StatusEnum } from "src/common/enum";
import Transformer from "src/common/Transformer";
import { NavigationItemsContext } from "./NavigationItemsList";
import { Grid, TextField } from "@mui/material";
import { FileInput } from "../../../components/file-input/FileInput";
import { useEditNodeController } from "src/enterprise/ra-tree/controllers/useEditNodeController";

export function NavigationItemsEdit() {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const controller = useEditNodeController();
  const { navigationId } = useParams();

  const [type, setType] = React.useState("");
  const [reference, setReference] = React.useState("");
  const [optionText, setOptionText] = React.useState("");
  const [entity, setEntity] = React.useState("");
  const [title, setTitle] = React.useState("");

  useEffect(() => {
    if (controller.record !== undefined) {
      if (controller.record.entity && entity === "") {
        setEntity(controller.record.entity);
      }
      if (controller.record.title && title === "") {
        setTitle(controller.record.title);
      }
    }
  }, [controller]);

  useEffect(() => {
    setOptionText("title");
    if (type === "page") {
      setReference("admin/pages");
    } else if (type === "article") {
      setReference("admin/articles");
    } else if (type === "articleCategory") {
      setReference("admin/article/categories");
      setOptionText("name");
    } else if (type === "product") {
      setReference("admin/products");
      setOptionText("sku");
    } else if (type === "productCategory") {
      setReference("admin/product/categories");
      setOptionText("name");
    }
  }, [type]);

  const setTitleAndEntity = (id) => {
    setEntity(id);

    if (type === "productCategory") {
      dataProvider
        .getOne("admin/product/categories", {
          id: id,
        })
        .then((res) => {
          setTitle(res.data.name);
        });
    } else if (type === "articleCategory") {
      dataProvider
        .getOne("admin/article/categories", {
          id: id,
        })
        .then((res) => {
          setTitle(res.data.name);
        });
    } else if (type === "product") {
      dataProvider
        .getOne("admin/products", {
          id: id,
        })
        .then((res) => {
          setTitle(res.data.name);
        });
    } else if (type !== "basic") {
      dataProvider
        .getOne(type + "s", {
          id: id,
        })
        .then((res) => {
          setTitle(res.data.title);
        });
    }
  };

  const transform = async (data) => {
    let parent = data.parent?.id;
    Transformer(data, {
      remove: ["id", "createdAt", "updatedAt", "entity", "parent", "children"],
      update: {
        openInNewTab:
          data.open_in_new_tab != null
            ? data.open_in_new_tab.toString()
            : "false",
        navigation: data.navigation.id,
        type: type,
        url: type === "basic" ? data.url : null,
        title: title,
        visibleByRoles: data.visible_by_roles.filter((id) =>
          RoleEnum.some((role) => role.id === id)
        ),
      },
    });

    if (entity && type !== "basic") {
      data.entity = entity;
    }

    if (parent) {
      data.parent = parent;
    }

    return data;
  };

  function NavigationItemsEditToolbar(props: ToolbarProps) {
    return (
      <Toolbar {...props} sx={{justifyContent: "space-between"}}>
        <SaveButton alwaysEnable/>
        <DeleteMenuItem confirmTitle={`Delete Item`} mutationMode="pessimistic" className="removeButton"/>
      </Toolbar>
    );
  }

  const success = (data) => {
    notify(`Navigation item edited successfully`);
    //window.location.reload();
  };

  return (
    <EditNode
      key={`navigation-item-${controller?.record?.id}`}
      transform={transform}
      mutationMode="pessimistic"
      resource={`admin/navigations/${navigationId}/items`}
    >
      <SimpleForm toolbar={<NavigationItemsEditToolbar />}>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item sm={12} lg={8}>
            <TextInput source="id" disabled />
          </Grid>
          <Grid item sm={12} lg={8}>
            <SelectInput
              source="type"
              validate={required()}
              choices={NavigationTypeEnum}
              onChange={(e) => {
                setType(e.target.value);
              }}
            />
          </Grid>

          {type !== "basic" && type !== "" ? (
            <Grid item sm={12} lg={8}>
              {reference !== "" ? (
                <ReferenceInput source="entity" reference={reference}>
                  <AutocompleteInput
                    filterToQuery={(search) => ({ search })}
                    onChange={(e) => setTitleAndEntity(e)}
                    size="small"
                    optionText={optionText}
                  />
                </ReferenceInput>
              ) : null}
            </Grid>
          ) : null}

          <Grid item sm={12} lg={8}>
            <TextField
              variant="outlined"
              label="Title"
              required
              size="small"
              onChange={(e) => setTitle(e.target.value)}
              sx={{ width: "100%", marginBottom: "24px" }}
              value={title}
            />
          </Grid>
          {type === "basic" ? (
            <Grid item sm={12} lg={8}>
              <TextInput source="url" />
            </Grid>
          ) : null}

          <Grid item sm={12} lg={8}>
            <TextInput
              source="description"
              multiline
              resettable
              sx={{
                textarea: { minHeight: "90px" },
              }}
            />
          </Grid>
          <Grid item sm={12} lg={8}>
            <FileInput
              label="Image"
              source="image"
              fileTypes={["JPG", "PNG", "GIF", "MP4"]}
              multiple={false}
              path="admin/media/common"
            />
          </Grid>
          <Grid item sm={12} lg={8}>
            <SelectInput
              source="status"
              validate={required()}
              choices={StatusEnum}
            />
          </Grid>
          <Grid item sm={12} lg={8}>
            <BooleanInput source="open_in_new_tab" />
          </Grid>
          <Grid item sm={12} lg={8}>
            <SelectArrayInput source="visible_by_roles" choices={RoleEnum} />
          </Grid>
        </Grid>

        <NavigationItemsEditDataInitializer
          setType={setType}
          setTitle={setTitle}
        />
      </SimpleForm>
    </EditNode>
  );
}

const NavigationItemsEditDataInitializer = ({ setType, setTitle }) => {
  const record = useRecordContext();
  const { setCurrentParent } = useContext(NavigationItemsContext);

  useEffect(() => {
    setType(record.type);
    setTitle(record.title);
    setCurrentParent(record);
  }, [record]);

  return null;
};
